import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ListOrdered, PlusCircle, ExternalLink } from "lucide-react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { ManualResolutionLogDialog } from "./ManualResolutionLogDialog";

interface Props {
  unaId: string;
}

interface RegistryRow {
  id: string;
  resolution_number: number;
  source: "portal_generated" | "manual_upload";
  title: string;
  adopted_date: string;
  resolution_id: string | null;
  record_document_id: string | null;
  governance_document_id: string | null;
}

/**
 * Resolution number registry list. Shows every allocated number for the
 * association, whether from a portal workflow or a manually logged paper
 * resolution. Numbers are unique; gaps are allowed for backfilling.
 */
export function ResolutionRegistryList({ unaId }: Props) {
  const [logOpen, setLogOpen] = useState(false);

  const { data, isLoading, refetch } = useQuery<RegistryRow[]>({
    queryKey: ["resolution_registry", unaId],
    queryFn: async () => {
      const { data, error } = await (supabase.from as any)("resolution_registry")
        .select(
          "id, resolution_number, source, title, adopted_date, resolution_id, record_document_id, governance_document_id",
        )
        .eq("una_id", unaId)
        .order("resolution_number", { ascending: true });
      if (error) throw error;
      return (data || []) as RegistryRow[];
    },
    enabled: !!unaId,
  });

  // Every allocated row lives on the Governance Record tab. Deep-link there
  // so the member lands on a page that exists today.
  const buildLink = (_row: RegistryRow): string | null => {
    return `/governance/record`;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <ListOrdered className="h-5 w-5 text-primary" />
            <CardTitle>Resolution number registry</CardTitle>
            {data && data.length > 0 && <Badge variant="secondary">{data.length}</Badge>}
          </div>
          <Button variant="outline" size="sm" onClick={() => setLogOpen(true)}>
            <PlusCircle className="h-4 w-4 mr-2" />
            Log a manual resolution
          </Button>
        </div>
        <CardDescription>
          One number per resolution, unique to your association. Portal-signed
          resolutions take the next number automatically; older paper
          resolutions can be logged here with any missing number.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : !data || data.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            No resolutions numbered yet.
          </p>
        ) : (
          <div className="divide-y">
            {data.map((row) => {
              const link = buildLink(row);
              return (
                <div
                  key={row.id}
                  className="flex items-center justify-between py-2 gap-3 flex-wrap"
                >
                  <div className="flex items-baseline gap-3 min-w-0">
                    <span className="font-mono text-sm tabular-nums text-muted-foreground w-14">
                      #{row.resolution_number}
                    </span>
                    <span className="font-medium truncate">{row.title}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <span>{format(new Date(row.adopted_date), "MMM d, yyyy")}</span>
                    <Badge variant="outline" className="text-xs">
                      {row.source === "portal_generated" ? "Portal" : "Manual"}
                    </Badge>
                    {link && (
                      <Link
                        to={link}
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                      >
                        Open <ExternalLink className="h-3 w-3" />
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <ManualResolutionLogDialog
        unaId={unaId}
        open={logOpen}
        onOpenChange={setLogOpen}
        onLogged={() => refetch()}
      />
    </Card>
  );
}
